import { createAsyncThunk } from "@reduxjs/toolkit";

import { toggleModal, VERIFY_GEOJSON_MODAL } from "../modals";
import { MapStructs, FeaturesInterface } from "../../../models/map-interface";
import { DataError } from "../../../utils/data";
import { getFilteredFeatures } from "../../../utils";
import { RootState } from "../../index";

import { setMapStructs, removeLane } from ".";
import { setFeatures } from "../currentMap";

export const setMapStructsData = createAsyncThunk(
  "appState/setMapStructsData",
  async (data: MapStructs & { errors?: Array<DataError> }, thunkAPI) => {
    const { lanes, stopSigns, intersections, controlLines, errors } = data;

    if (errors && errors.length !== 0) {
      thunkAPI.dispatch(
        toggleModal({ type: VERIFY_GEOJSON_MODAL, data: errors })
      );
    }
    thunkAPI.dispatch(
      setMapStructs({ lanes, stopSigns, intersections, controlLines })
    );
  }
);

export const removeLaneAndUpdateFeatures = createAsyncThunk(
  "mapStructs/removeLaneAndUpdateFeatures",
  async (
    { laneId, mapFeatures }: { laneId: number; mapFeatures: FeaturesInterface },
    thunkAPI
  ) => {
    // Store original state for potential rollback
    const originalState = {
      mapStructs: (thunkAPI.getState() as RootState).mapStructs,
      mapFeatures: (thunkAPI.getState() as RootState).currentMap.mapFeatures,
    };

    try {
      // Validate inputs
      if (typeof laneId !== "number" || isNaN(laneId)) {
        throw new Error(`Invalid laneId: ${laneId}. Must be a valid number.`);
      }

      if (
        !mapFeatures ||
        !mapFeatures.features ||
        !Array.isArray(mapFeatures.features)
      ) {
        throw new Error("Invalid mapFeatures: features array is required.");
      }

      // Check if lane exists before attempting to remove
      const currentMapStructs = (thunkAPI.getState() as RootState).mapStructs;
      if (!currentMapStructs.lanes || !currentMapStructs.lanes[laneId]) {
        throw new Error(`Lane with ID ${laneId} does not exist.`);
      }

      // Check if lane is being used by other map structures (intersections, stop signs, etc.)
      const laneUsage: string[] = [];
      if (currentMapStructs.intersections) {
        Object.values(currentMapStructs.intersections).forEach(
          (intersection: any) => {
            if (
              intersection.associated_lane_ids &&
              intersection.associated_lane_ids.includes(laneId)
            ) {
              laneUsage.push(`Intersection ${intersection.intersection_id}`);
            }
          }
        );
      }

      if (currentMapStructs.stopSigns) {
        Object.values(currentMapStructs.stopSigns).forEach((stopSign: any) => {
          if (stopSign.associated_lane_id === laneId) {
            laneUsage.push(`Stop Sign ${stopSign.stop_sign_id}`);
          }
        });
      }

      if (currentMapStructs.controlLines) {
        Object.values(currentMapStructs.controlLines).forEach(
          (controlLine: any) => {
            if (controlLine.associated_lane_id === laneId) {
              laneUsage.push(`Control Line ${controlLine.control_line_id}`);
            }
          }
        );
      }

      if (laneUsage.length > 0) {
        throw new Error(
          `Cannot remove lane ${laneId} as it is being used by: ${laneUsage.join(
            ", "
          )}`
        );
      }

      // Remove lane from mapStructs
      thunkAPI.dispatch(removeLane(laneId));

      // Update features to remove lane associations
      const updatedFeatures = {
        ...mapFeatures,
        features: getFilteredFeatures(mapFeatures.features, laneId),
      };

      // Validate the updated features
      if (
        !updatedFeatures.features ||
        !Array.isArray(updatedFeatures.features)
      ) {
        throw new Error(
          "Failed to update features: invalid features array returned."
        );
      }

      thunkAPI.dispatch(setFeatures(updatedFeatures));

      return { success: true, laneId };
    } catch (error) {
      // Rollback changes on error
      console.error("Error in removeLaneAndUpdateFeatures:", error);

      try {
        // Restore original mapStructs state
        thunkAPI.dispatch(setMapStructs(originalState.mapStructs));

        // Restore original mapFeatures state
        thunkAPI.dispatch(setFeatures(originalState.mapFeatures));

        console.log("Successfully rolled back changes after error");
      } catch (rollbackError) {
        console.error("Failed to rollback changes:", rollbackError);
        // Even if rollback fails, we still want to reject with the original error
      }

      // Reject the thunk with error information
      return thunkAPI.rejectWithValue({
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
        laneId,
        originalState,
      });
    }
  }
);
